import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { AuthShell, AuthHeading } from '../../components/auth/auth-shell';
import { useResendVerification } from '../../hooks/use-resend-verification';

type FormData = { email: string };

export default function ResendVerificationPage() {
  const [isSent, setIsSent] = useState(false);
  const resend = useResendVerification();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  if (isSent) return <SentView />;

  const onSubmit = (data: FormData) => {
    resend.mutate(data.email, { onSuccess: () => setIsSent(true) });
  };

  return (
    <AuthShell>
      <AuthHeading
        title="Send a new verification link"
        description="Enter the email you registered with and we'll send you a new link."
      />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-title font-medium">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            {...register('email', { required: 'Email is required' })}
            className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
            style={{ fontSize: '16px' }}
          />
          {errors.email && (
            <p className="text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>
        <Button
          type="submit"
          disabled={resend.isPending}
          className="w-full h-12 bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center"
        >
          {resend.isPending ? <LoadingSpinner /> : 'Send a new link'}
        </Button>
      </form>
      <BackToSignInLink />
    </AuthShell>
  );
}

function SentView() {
  return (
    <AuthShell>
      <AuthHeading
        title="Check your email"
        description="If an account exists for that email, we've sent a new verification link."
      />
      <BackToSignInLink />
    </AuthShell>
  );
}

function BackToSignInLink() {
  return (
    <div className="text-center">
      <Link
        to="/sign-in"
        className="text-sm text-accent-purple hover:text-accent-purple/80"
      >
        Back to sign in
      </Link>
    </div>
  );
}
